import React, { useState } from 'react';
import { t } from '../../../locales/i18n';
import { fromNow, naturalShortTime, parseISO } from '../../../utils/date-utils';
import { Comments } from '../../../components/comments';
import { ConfirmButton, ConfirmIcon } from '../../../components/widgets/confirm-button';
import { APITicket } from '../../../types/ticket';
import styled from 'styled-components';
import { Tooltip } from '../../../components/widgets/tooltip';
import { InputWrapper } from '../../../components/forms/input-wrapper';
import { Input, Button } from 'semantic-ui-react';
import { Segment } from '../../../components/segment';
import { sizes } from '../../../themes/sizes';
import { colors } from '@nextinq/ui-toolkit';
import { Icon } from '../../../components/icon';

const Card = styled.section`
  display: grid;
  align-items: center;
  padding: ${sizes.margins.small};
  grid-template-columns: 80px 100px 1fr 100px;
  grid-template-rows: auto auto auto auto;
  grid-template-areas:
    '  .      time    status   status '
    'avatar   time      .        .    '
    'avatar   note     note     note  '
    '  .     actions  actions  actions';
`;
const Status = styled.section`
  grid-area: status;
  justify-self: end;
  color: ${colors.grey.grey};
  display: grid;
  grid-auto-flow: column;
  align-items: center;
`;
const AvatarWrapper = styled.section`
  grid-area: avatar;
  justify-self: center;
`;

const ServiceName = styled.div`
  padding: 1px 3px;
  border-radius: 3px;
  background-color: ${colors.grey.greyLighter};
  color: ${colors.neutral.white};
  font-size: smaller;
`;

const Avatar = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background-color: ${colors.base.primary};
  color: ${colors.neutral.white};
  font-size: ${sizes.fonts.large};
`;
const Time = styled.section`
  grid-area: time;
  gap: ${sizes.margins.normal};
  h5,
  h6 {
    padding-right: ${sizes.margins.normal};
  }
`;
const NoteWrapper = styled.section`
  grid-area: note;
  i {
    font-size: ${sizes.fonts.large};
    color: ${colors.grey.greyLighter};
  }
`;
const Note = styled.strong``;
const Actions = styled.section`
  grid-area: actions;
  justify-self: end;
`;

interface TicketItemProps {
  queueId: string;
  ticket: APITicket;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  comments: Array<any>;
  onUpdateTicket: (ticket: APITicket) => void;
  onRemoveSpecificTicket: (queueId: string, ticketUUID: string) => void;
  onCallSpecificTicket: (queueId: string, ticketUUID: string) => void;
}

const TooltipContainer = styled.div`
  display: grid;
  grid-gap: 9px;
  padding: 1em;
`;

function RemoteTicketTooltip(props: { ticket: APITicket }) {
  const { ticket } = props;
  if (!ticket) {
    return null;
  }
  const isRemote = ticket.origin === 'remote-checkin';
  return (
    <TooltipContainer>
      <h4>
        {isRemote && <Icon name="mobile alternate" />}
        <strong>{ticket.formattedNumber}</strong>, PIN: <strong>{ticket.pin}</strong>
      </h4>
      {ticket.sharingInfo && (
        <>
          {ticket.sharingInfo.fullName && <h5>{ticket.sharingInfo.fullName}</h5>}
          {ticket.sharingInfo.phoneNumber && (
            <h5>
              <a href={`tel:${ticket.sharingInfo.phoneNumber}`}>{ticket.sharingInfo.phoneNumber}</a>
            </h5>
          )}
          {ticket.sharingInfo.email && (
            <h5>
              <a href={`mailto:${ticket.sharingInfo.email}`} target="_blank" rel="noopener noreferrer">
                {ticket.sharingInfo.email}
              </a>
            </h5>
          )}
        </>
      )}
    </TooltipContainer>
  );
}

export function TicketItem(props: TicketItemProps) {
  const { ticket } = props;
  const [ticketNote, setTicketNote] = useState(ticket.note || '');

  const isRemote = ticket.origin === 'remote-checkin';
  return (
    <Segment padding="medium">
      <Card>
        <Status>
          {ticket.serviceName && <ServiceName>{ticket.serviceName}</ServiceName>}
          {isRemote && <Icon name="mobile alternate" />}
          <Tooltip title={`PIN: ${ticket.pin}`} triggerIconName="tag">
            <RemoteTicketTooltip ticket={ticket} />
          </Tooltip>
        </Status>
        <AvatarWrapper>
          <Avatar>{ticket.formattedNumber}</Avatar>
        </AvatarWrapper>
        <Time>
          <h5>{naturalShortTime(parseISO(ticket.createdOn))}</h5>
          <h6>{fromNow(parseISO(ticket.createdOn))}</h6>
        </Time>

        <NoteWrapper>
          <ConfirmIcon
            iconName="comment"
            onConfirm={() => props.onUpdateTicket({ ...ticket, note: ticketNote })}
            title={t('updateTicket.title')}
            subtitle={t('updateTicket.subtitle')}
          >
            <InputWrapper label={t('updateTicket.hintText')} helperText={t('updateTicket.floatingLabel')}>
              <Input
                maxLength={20}
                fullWidth
                onChange={(e) => setTicketNote(e.target.value)}
                value={ticketNote || ''}
              />
            </InputWrapper>
          </ConfirmIcon>
          {ticket.note && <Note>{ticket.note}</Note>}
          {false && <Comments simpleView allowAdd={isRemote} ticketUUID={ticket.uuid} ticketNumber={ticket.number} />}
        </NoteWrapper>
        <Actions>
          <ConfirmButton
            basic
            negative
            size="mini"
            label={t('button.removeTicket')}
            title={t('queueTickets.removeTicketTitle')}
            subtitle={t('queueTickets.removeTicketHint', {
              number: ticket.number,
              queueId: props.queueId
            })}
            onConfirm={() => props.onRemoveSpecificTicket(props.queueId, ticket.uuid)}
          />
          <Button basic size="mini" onClick={() => props.onCallSpecificTicket(props.queueId, ticket.uuid)}>
            {t('queueTickets.callTicket')}
          </Button>
        </Actions>
      </Card>
    </Segment>
  );
}
